import { useState, useRef, useCallback, MutableRefObject } from 'react';
import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import EventsService from '@services/EventsService';
import AssistantQueryService from '@services/AssistantQueryService';
import { IAssistantScript } from '@interfaces/assistantScript';
import { IEventSettingsAiAssistant } from '@interfaces/event';
import { PollyLanguage } from '@shared/interfaces/polly';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import { log } from './Debug';
import {
  isLandingTrigger,
  isAITrigger,
  resolveJoinTriggerName,
} from './providerUtils';
import { selectVoiceForLanguage } from '@constants/polly';
import { toPollyLanguageCode } from '@constants/language';
import { replaceAssistantPlaceholders } from '@shared/utils/assistantScript';
import {
  assistantMapKey,
  matchesAssistantMap,
} from '@shared/utils/assistantMap';
import {
  debugEventId,
  debugMapName,
  enableScriptAudioPreload,
  PRELOAD_SCRIPTS_CAP,
} from '@core/assistant/constants';

export type GetEffectiveAiSettingsFn = () => {
  engine: string;
  voiceId: string;
  language: PollyLanguage;
  baseLanguage: PollyLanguage;
  languages: PollyLanguage[] | undefined;
  voicesByLanguage?: IEventSettingsAiAssistant['voicesByLanguage'];
};

export type UseAssistantScriptsParams = {
  mytaverseEventRef: MutableRefObject<any>;
  activeBrandingIdRef: MutableRefObject<string | undefined>;
  selectedLanguageRef: MutableRefObject<string>;
  preRenderedAudioRef: MutableRefObject<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >;
  debugEventAiSettingsRef: MutableRefObject<IEventSettingsAiAssistant | null>;
  userNameRef: MutableRefObject<string | undefined>;
  getEffectiveAiSettings: GetEffectiveAiSettingsFn;
};

export const useAssistantScripts = ({
  mytaverseEventRef,
  activeBrandingIdRef,
  selectedLanguageRef,
  preRenderedAudioRef,
  debugEventAiSettingsRef,
  userNameRef,
  getEffectiveAiSettings,
}: UseAssistantScriptsParams) => {
  const [scripts, setScripts] = useState<IAssistantScript[]>([]);

  const scriptsRef = useRef(scripts);
  scriptsRef.current = scripts;

  const preloadScriptsAudio = useCallback(
    async (filteredScripts: IAssistantScript[]) => {
      if (!enableScriptAudioPreload) {
        return;
      }

      if (!mytaverseEventRef.current?.currentEventId && !debugEventId) {
        return;
      }

      const effectiveAiSettings = getEffectiveAiSettings();
      const langKey = toPollyLanguageCode(
        selectedLanguageRef.current,
      ) as PollyLanguage;

      const joinTriggerName = resolveJoinTriggerName(
        mytaverseEventRef.current?.currentRoom?.map?.regions,
      );
      const landingScripts = filteredScripts.filter((s) =>
        isLandingTrigger(s.scriptTrigger, joinTriggerName),
      );
      const regionScripts = filteredScripts.filter(
        (s) =>
          !isLandingTrigger(s.scriptTrigger, joinTriggerName) &&
          !isAITrigger(s.scriptTrigger) &&
          s.triggerType !== 'object',
      );
      const aiTriggerScripts = filteredScripts.filter((s) =>
        isAITrigger(s.scriptTrigger),
      );
      const poiScripts = filteredScripts.filter(
        (s) => s.triggerType === 'object',
      );

      log('[preload] script counts', {
        landing: landingScripts.length,
        region: regionScripts.length,
        aiTrigger: aiTriggerScripts.length,
        poi: poiScripts.length,
      });

      let scriptsPreloaded = 0;

      const preloadScript = async (
        script: IAssistantScript,
        maxSteps: number | null,
      ) => {
        if (scriptsPreloaded >= PRELOAD_SCRIPTS_CAP) {
          return;
        }

        let parsedNodes: ScriptNodeJSON[];

        try {
          parsedNodes = JSON.parse(script.scriptData || '[]');
        } catch (error) {
          log('[preload] failed to parse script data', {
            scriptId: script.scriptId,
            error,
          });
          return;
        }

        if (!parsedNodes.length) {
          return;
        }

        const nodesToPreload =
          maxSteps !== null ? parsedNodes.slice(0, maxSteps) : parsedNodes;
        const area = Array.isArray(script.scriptTrigger)
          ? script.scriptTrigger[0]
          : script.scriptTrigger;

        const allowedByEvent =
          !effectiveAiSettings.languages?.length ||
          effectiveAiSettings.languages.includes(langKey);
        const allStepsTranslated =
          allowedByEvent &&
          parsedNodes.every((node) => !!node.translations?.[langKey]);

        scriptsPreloaded++;

        for (const [index, node] of nodesToPreload.entries()) {
          let text =
            allStepsTranslated && node.translations?.[langKey]
              ? node.translations[langKey]
              : node.text;
          let engine = effectiveAiSettings.engine;
          let voiceId = effectiveAiSettings.voiceId;
          let language: PollyLanguage = effectiveAiSettings.baseLanguage;

          if (allStepsTranslated) {
            const selected = selectVoiceForLanguage(
              langKey,
              effectiveAiSettings.voicesByLanguage,
            );
            language = langKey;
            engine = selected.engine;
            voiceId = selected.voiceId;
          }

          text = replaceAssistantPlaceholders(
            text,
            userNameRef.current,
            mytaverseEventRef.current.currentEvent?.name,
          );

          if (
            preRenderedAudioRef.current.find(
              (x) =>
                x.text === text &&
                x.engine === engine &&
                x.voiceId === voiceId &&
                x.language === language,
            )
          ) {
            continue;
          }

          preRenderedAudioRef.current.push({
            area,
            index: index + 1,
            audio: null,
            text,
            engine,
            voiceId,
            language,
          });

          try {
            const arrayBuffer =
              await AssistantQueryService.generateAssistantAudio({
                text,
                disableOptimizations: true,
                engine,
                voiceId,
                language,
              });

            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
              log('[preload] empty audio response', {
                area,
                step: index + 1,
                scriptId: script.scriptId,
              });
              continue;
            }

            const entry = preRenderedAudioRef.current.find(
              (x) =>
                x.text === text &&
                x.engine === engine &&
                x.voiceId === voiceId &&
                x.language === language,
            );

            if (entry) {
              entry.audio = arrayBuffer;
            }

            log(
              `[preload] "${area}" step ${index + 1}: ${(
                arrayBuffer.byteLength / 1024
              ).toFixed(2)} KB`,
            );
          } catch (error) {
            log('[preload] failed to fetch audio', {
              area,
              step: index + 1,
              scriptId: script.scriptId,
              error,
            });
          }
        }
      };

      for (const script of landingScripts) {
        await preloadScript(script, null);
      }

      for (const script of regionScripts) {
        await preloadScript(script, 1);
      }

      for (const script of aiTriggerScripts) {
        await preloadScript(script, 1);
      }

      for (const script of poiScripts) {
        await preloadScript(script, 1);
      }

      log('[preload] completed', {
        scriptsPreloaded,
        totalCached: preRenderedAudioRef.current.length,
      });
    },
    [],
  );

  const fetchScripts = useCallback(async () => {
    const fetchedScripts = await AssistantScriptsService.getAssistantScripts();

    log('loaded assistant scripts', fetchedScripts);

    // Scope by the canonical map key (ueIdentifier); `matchesAssistantMap`
    // still accepts records written with the map's display name, so a rename
    // does not orphan them (MV-1891).
    const currentMap =
      mytaverseEventRef.current?.currentRoom?.map ?? debugMapName;
    const currentMapName = assistantMapKey(currentMap);
    const currentBrandingId =
      mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
      activeBrandingIdRef.current;

    const filteredScripts = fetchedScripts.filter((s) => {
      if (currentMapName && !matchesAssistantMap(s.mapName, currentMap)) {
        return false;
      }

      if (currentBrandingId && s.brandingId !== currentBrandingId) {
        return false;
      }

      return true;
    });

    // Button-driven scripts (ui_click / custom_spot) are always
    // non_interruptible: the user starts them explicitly via the button, and
    // once playing a region change must not cut the tour off. Coerce here — the
    // single point every consumer (scriptsRef, the region-handler interrupt
    // gates) reads from — so legacy scripts saved with finish_step/hard_cut
    // behave correctly without a re-save.
    const normalizedScripts = filteredScripts.map((script) =>
      script.triggerType === 'ui_click' || script.triggerType === 'custom_spot'
        ? { ...script, interruptMode: 'non_interruptible' as const }
        : script,
    );

    log('filtered assistant scripts', {
      currentMapName,
      currentBrandingId,
      count: normalizedScripts.length,
    });

    if (!mytaverseEventRef.current?.currentEventId && debugEventId) {
      try {
        const eventParticipantData = await EventsService.getEvent(debugEventId);
        const aiAssistantSettings =
          eventParticipantData?.event?.settings?.aiAssistant;

        if (aiAssistantSettings) {
          debugEventAiSettingsRef.current = aiAssistantSettings;
          log('loaded debug event AI assistant settings', aiAssistantSettings);
        }
      } catch (error) {
        log('failed to load debug event AI assistant settings', error);
      }
    }

    setScripts(normalizedScripts);
    void preloadScriptsAudio(normalizedScripts);
    return normalizedScripts;
  }, []);

  return {
    scripts,
    setScripts,
    scriptsRef,
    fetchScripts,
    preloadScriptsAudio,
  };
};
